import * as React from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getMyAdminStatus } from "@/lib/billing.functions";
import { CreateProspectDialog } from "@/components/admin/create-prospect-dialog";
import { resolveDealContact } from "@/lib/contacts.functions";
import {
  listAllUsersAdmin,
  sendRatingRequestAdmin,
  type AdminUserRow,
  type RatingRequestResult,
} from "@/lib/admin-users.functions";

type SortType = "text" | "date" | "number" | "boolean";

const ts = (v: string | null): number | null =>
  v ? new Date(v).getTime() : null;

const SORT_COLUMNS: Record<
  string,
  { label: string; type: SortType; value: (r: AdminUserRow) => string | number | null }
> = {
  email: {
    label: "Email",
    type: "text",
    value: (r) => (r.email ? r.email.toLowerCase() : null),
  },
  name: {
    label: "Name",
    type: "text",
    value: (r) => {
      const n = `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim();
      return n ? n.toLowerCase() : null;
    },
  },
  firm: {
    label: "Firm",
    type: "text",
    value: (r) => (r.firm ? r.firm.toLowerCase() : null),
  },
  created_at: { label: "Signed up", type: "date", value: (r) => ts(r.created_at) },
  confirmed: {
    label: "Confirmed",
    type: "date",
    value: (r) => ts(r.email_confirmed_at),
  },
  email_verified: {
    label: "Email verified",
    type: "boolean",
    value: (r) => (r.email_verified ? 1 : 0),
  },
  last_sign_in: {
    label: "Last sign-in",
    type: "date",
    value: (r) => ts(r.last_sign_in_at),
  },
  last_activity: {
    label: "Last activity",
    type: "date",
    value: (r) => ts(r.last_activity_at),
  },
  credits: {
    label: "Credits",
    type: "number",
    value: (r) => r.credits_remaining ?? 0,
  },
  team: {
    label: "Team",
    type: "text",
    value: (r) => (r.team_name ? r.team_name.toLowerCase() : null),
  },
  deals: { label: "Deals", type: "number", value: (r) => r.deals_count },
};

type SortKey = keyof typeof SORT_COLUMNS;

export const Route = createFileRoute("/_authed/admin/users")({
  head: () => ({
    meta: [
      { title: "Admin users: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const fetchAllUsers = useServerFn(listAllUsersAdmin);
  const sendRatingReq = useServerFn(sendRatingRequestAdmin);
  const resolveContactFn = useServerFn(resolveDealContact);
  const navigate = useNavigate();

  const allUsers = useQuery({
    queryKey: ["admin-all-users"],
    queryFn: () => fetchAllUsers(),
  });

  const [userSearch, setUserSearch] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [pendingRecipients, setPendingRecipients] = React.useState<
    { id: string; email: string | null }[]
  >([]);
  const [ratingResults, setRatingResults] = React.useState<
    RatingRequestResult[] | null
  >(null);
  const [ratingError, setRatingError] = React.useState<string | null>(null);

  const userRows = allUsers.data?.rows ?? [];
  const unconfirmedCount = userRows.filter((r) => !r.email_confirmed_at).length;
  const q = userSearch.trim().toLowerCase();
  const matchedUsers = q
    ? userRows.filter((r) => {
        const name = `${r.first_name ?? ""} ${r.last_name ?? ""}`.toLowerCase();
        return (
          (r.email ?? "").toLowerCase().includes(q) ||
          name.includes(q) ||
          (r.firm ?? "").toLowerCase().includes(q)
        );
      })
    : userRows;

  const [sort, setSort] = React.useState<{
    key: SortKey;
    dir: "asc" | "desc";
  } | null>(null);

  const onSortClick = React.useCallback((key: SortKey) => {
    setSort((cur) =>
      cur && cur.key === key
        ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { key, dir: SORT_COLUMNS[key].type === "text" ? "asc" : "desc" },
    );
  }, []);

  const filteredUsers = React.useMemo(() => {
    if (!sort) return matchedUsers;
    const col = SORT_COLUMNS[sort.key];
    const mult = sort.dir === "asc" ? 1 : -1;
    return [...matchedUsers].sort((a, b) => {
      const va = col.value(a);
      const vb = col.value(b);
      const aNull = va === null;
      const bNull = vb === null;
      // Nulls / "Never" always last, regardless of direction.
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let cmp: number;
      if (typeof va === "string" && typeof vb === "string") {
        cmp = va.localeCompare(vb);
      } else {
        cmp = (va as number) < (vb as number)
          ? -1
          : (va as number) > (vb as number)
            ? 1
            : 0;
      }
      if (cmp !== 0) return cmp * mult;
      return b.created_at.localeCompare(a.created_at);
    });
  }, [matchedUsers, sort]);

  const filteredIds = React.useMemo(
    () => filteredUsers.map((u) => u.id),
    [filteredUsers],
  );
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someFilteredSelected =
    !allFilteredSelected && filteredIds.some((id) => selectedIds.has(id));

  const toggleAllFiltered = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) for (const id of filteredIds) next.add(id);
      else for (const id of filteredIds) next.delete(id);
      return next;
    });
  };
  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const openConfirmForIds = (ids: string[]) => {
    const recipients = ids
      .map((id) => {
        const row = userRows.find((r) => r.id === id);
        return { id, email: row?.email ?? null };
      })
      .filter((r) => r.email);
    setPendingRecipients(recipients);
    setRatingResults(null);
    setRatingError(null);
  };

  const ratingMutation = useMutation({
    mutationFn: (ids: string[]) => sendRatingReq({ data: { userIds: ids } }),
    onSuccess: (res) => {
      setRatingResults(res.results);
      setSelectedIds(new Set());
    },
    onError: (e) =>
      setRatingError(e instanceof Error ? e.message : "Send failed"),
  });

  const dialogOpen = pendingRecipients.length > 0;
  const closeDialog = () => {
    if (ratingMutation.isPending) return;
    setPendingRecipients([]);
    setRatingResults(null);
    setRatingError(null);
  };
  const sentCount = ratingResults?.filter((r) => r.status === "sent").length ?? 0;
  const failedResults = ratingResults?.filter((r) => r.status !== "sent") ?? [];

  const fmtShortDate = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const [lookupId, setLookupId] = React.useState("");
  const [lookupError, setLookupError] = React.useState<string | null>(null);
  const lookupMutation = useMutation({
    mutationFn: () =>
      resolveContactFn({ data: { contactId: lookupId.trim() } }),
    onSuccess: (res) => {
      setLookupError(null);
      setLookupId("");
      navigate({
        to: "/deals/$dealId/investors/$contactId",
        params: { dealId: res.dealId, contactId: res.contactId },
      });
    },
    onError: (e) =>
      setLookupError(e instanceof Error ? e.message : "Lookup failed"),
  });

  return (
    <main className="mx-auto max-w-[1200px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Users.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          All accounts across the platform. Display only. Last activity
          excludes internal and test activity.
        </p>
      </header>

      <section className="mb-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        {allUsers.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : allUsers.error ? (
          <p className="text-[12.5px] text-destructive">
            {allUsers.error instanceof Error
              ? allUsers.error.message
              : "Failed to load users"}
          </p>
        ) : (
          <>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[12.5px] text-brand-muted">
                {userRows.length} total, {unconfirmedCount} unconfirmed
                {q ? ` · ${filteredUsers.length} match` : ""}
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search email, name, or firm"
                  className="sm:max-w-[280px]"
                />
                <CreateProspectDialog
                  onCreated={() => void allUsers.refetch()}
                />
              </div>
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={selectedIds.size === 0}
                onClick={() => openConfirmForIds(Array.from(selectedIds))}
              >
                Send rating request to selected ({selectedIds.size})
              </Button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-brand-line">
              <table className="w-full text-[13px]">
                <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                  <tr>
                    <th className="w-8 px-3 py-2">
                      <Checkbox
                        checked={
                          allFilteredSelected
                            ? true
                            : someFilteredSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={(v) => toggleAllFiltered(v === true)}
                        aria-label="Select all filtered users"
                      />
                    </th>
                    {(
                      [
                        ["email", false],
                        ["name", false],
                        ["firm", false],
                        ["created_at", false],
                        ["confirmed", false],
                        ["email_verified", false],
                        ["last_sign_in", false],
                        ["last_activity", false],
                        ["credits", true],
                        ["team", false],
                        ["deals", true],
                      ] as Array<[SortKey, boolean]>
                    ).map(([key, right]) => {
                      const active = sort?.key === key;
                      return (
                        <th
                          key={key}
                          className={`px-3 py-2 ${right ? "text-right" : ""}`}
                          aria-sort={
                            active
                              ? sort!.dir === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <button
                            type="button"
                            onClick={() => onSortClick(key)}
                            className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-brand-ink ${
                              active ? "text-brand-ink" : ""
                            }`}
                          >
                            {SORT_COLUMNS[key].label}
                            {active ? (
                              <span aria-hidden="true">
                                {sort!.dir === "asc" ? "▲" : "▼"}
                              </span>
                            ) : null}
                          </button>
                        </th>
                      );
                    })}
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td
                        className="px-3 py-4 text-center text-brand-muted"
                        colSpan={13}
                      >
                        No users match.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u: AdminUserRow) => {
                      const name =
                        `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
                      return (
                        <tr key={u.id} className="border-t border-brand-line">
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={selectedIds.has(u.id)}
                              onCheckedChange={(v) =>
                                toggleOne(u.id, v === true)
                              }
                              aria-label={`Select ${u.email ?? u.id}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Link
                              to="/admin/users/$userId"
                              params={{ userId: u.id }}
                              className="underline"
                              title="View this user's deals"
                            >
                              {u.email ?? u.id}
                            </Link>
                          </td>
                          <td className="px-3 py-2">{name || ""}</td>
                          <td className="px-3 py-2">{u.firm ?? ""}</td>
                          <td className="px-3 py-2 text-brand-muted">
                            {fmtShortDate(u.created_at)}
                          </td>
                          <td className="px-3 py-2">
                            {u.email_confirmed_at ? (
                              <span className="inline-flex items-center rounded-full bg-[oklch(0.94_0.05_155.7)] px-2 py-0.5 text-[11px] font-medium text-[oklch(0.35_0.087_155.7)]">
                                Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-[oklch(0.94_0.09_75)] px-2 py-0.5 text-[11px] font-medium text-[oklch(0.4_0.13_60)]">
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {u.email_verified ? (
                              <span className="inline-flex items-center rounded-full bg-[oklch(0.94_0.05_155.7)] px-2 py-0.5 text-[11px] font-medium text-[oklch(0.35_0.087_155.7)]">
                                Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-[oklch(0.94_0.09_75)] px-2 py-0.5 text-[11px] font-medium text-[oklch(0.4_0.13_60)]">
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-brand-muted">
                            {u.last_sign_in_at
                              ? fmtShortDate(u.last_sign_in_at)
                              : "Never"}
                          </td>
                          <td className="px-3 py-2 text-brand-muted">
                            {u.last_activity_at
                              ? fmtShortDate(u.last_activity_at)
                              : "Never"}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {u.credits_remaining ?? 0}
                          </td>
                          <td className="px-3 py-2 text-brand-muted">
                            {u.team_name
                              ? `${u.team_name}${u.team_role ? ` (${u.team_role})` : ""}`
                              : ""}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {u.deals_count}
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!u.email}
                              onClick={() => openConfirmForIds([u.id])}
                            >
                              Request rating
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <Dialog
              open={dialogOpen}
              onOpenChange={(o) => {
                if (!o) closeDialog();
              }}
            >
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {ratingResults
                      ? "Rating request results"
                      : `Send rating request to ${pendingRecipients.length} recipient${pendingRecipients.length === 1 ? "" : "s"}?`}
                  </DialogTitle>
                  {!ratingResults && (
                    <DialogDescription>
                      Sends the rating request email from hello@capitaldart.com,
                      reply-to kennymargolis@gmail.com.
                    </DialogDescription>
                  )}
                </DialogHeader>
                {!ratingResults ? (
                  <div className="max-h-[240px] overflow-y-auto rounded-md border border-brand-line bg-brand-paper-2 p-3 text-[12.5px]">
                    {pendingRecipients.length === 0 ? (
                      <p className="text-brand-muted">No valid recipients.</p>
                    ) : (
                      <ul className="space-y-0.5">
                        {pendingRecipients.map((r) => (
                          <li key={r.id} className="font-mono">
                            {r.email}
                          </li>
                        ))}
                      </ul>
                    )}
                    {ratingError && (
                      <p className="mt-2 text-destructive">{ratingError}</p>
                    )}
                  </div>
                ) : (
                  <div className="max-h-[280px] overflow-y-auto rounded-md border border-brand-line bg-brand-paper-2 p-3 text-[12.5px]">
                    <p className="mb-2">
                      Sent {sentCount} of {ratingResults.length}.
                    </p>
                    {failedResults.length > 0 && (
                      <>
                        <p className="mb-1 font-medium">Not sent:</p>
                        <ul className="space-y-0.5">
                          {failedResults.map((r) => (
                            <li key={r.userId} className="font-mono">
                              {r.email ?? r.userId}: {r.status}
                              {r.error ? ` (${r.error})` : ""}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
                <DialogFooter>
                  {!ratingResults ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={closeDialog}
                        disabled={ratingMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        disabled={
                          ratingMutation.isPending ||
                          pendingRecipients.length === 0
                        }
                        onClick={() =>
                          ratingMutation.mutate(
                            pendingRecipients.map((r) => r.id),
                          )
                        }
                      >
                        {ratingMutation.isPending
                          ? "Sending…"
                          : `Send to ${pendingRecipients.length}`}
                      </Button>
                    </>
                  ) : (
                    <Button type="button" onClick={closeDialog}>
                      Close
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </section>

      <section className="rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-1 font-serif text-[20px] text-brand-ink">
          Open contact by id
        </h2>
        <p className="mb-4 text-[12.5px] text-brand-muted">
          Paste a <code className="font-mono">deal_contacts.id</code> (uuid).
          The deal is resolved server-side, then you are navigated to that
          contact.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setLookupError(null);
            lookupMutation.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="lookup-id">deal_contacts.id</Label>
            <Input
              id="lookup-id"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              autoComplete="off"
            />
          </div>
          <Button
            type="submit"
            disabled={lookupMutation.isPending || !lookupId.trim()}
          >
            {lookupMutation.isPending ? "Looking up…" : "Open contact"}
          </Button>
          {lookupError && (
            <p className="text-[12.5px] text-destructive">{lookupError}</p>
          )}
        </form>
      </section>
    </main>
  );
}